/* ============================================================
   ZNAKI · glitch-gl.js
   Shader rozdartej tarczy — jedyny kawałek sceny liczony na GPU.

   Cała instalacja rysuje się w Canvasie 2D (renderer.js). Żeby nie
   przepisywać jej na WebGL dla ułamka sekundy efektu, glitch jedzie
   BOKIEM: mamy własne, ukryte płótno WebGL, na którym powstaje sama
   tarcza, a renderer wstawia je w scenę zwykłym drawImage().
   Dzięki temu figura zostaje w sortowaniu po głębi (belka pary
   "koło z belką" nadal kładzie się na wierzchu), zachowuje krycie
   i tryb mieszania, a reszta rysowania nic o WebGL nie wie.

   Układ shadera: promień tarczy = 1, quad sięga uHalf w każdą stronę
   (szerzej w pionie, bo pasy się rozjeżdżają). Mapowanie jest
   ODWROTNE — dla każdego piksela pytamy, z którego miejsca tarczy
   pochodzi — więc pasy nigdy na siebie nie nachodzą.

   Ponad podział na pasy (to potrafiły też prostokąty) shader dokłada
   rzeczy z piksela: rwanie krawędzi wzdłuż pasa, wygryzione ziarno
   i widmową smugę za przesuniętym pasem.

   Gdy WebGL jest niedostępny albo kontekst przepadnie, render()
   zwraca null i renderer wraca do wersji na prostokątach.
   ============================================================ */

import { PALETTE } from './config';

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

varying vec2 vUv;

uniform vec2  uHalf;      /* zasięg quada w promieniach tarczy */
uniform vec2  uPx;        /* rozmiar teksela w tych samych jednostkach */
uniform float uTime;      /* czas sceny [s] */
uniform float uSeed;      /* ziarno tego konkretnego glitcha */
uniform float uSlices;    /* liczba pionowych pasów */
uniform float uStrength;  /* siła rozjazdu (ułamek promienia) */
uniform float uSpeed;     /* przetasowania na sekundę */
uniform float uMono;      /* udział pasów bez pomarańczu */
uniform float uNoise;     /* faktura z piksela */
uniform float uSmear;     /* widmowa smuga za pasem */
uniform vec3  uOrange;
uniform vec3  uBlack;
uniform vec3  uWhite;

/* Haszowanie bez tekstur — odpowiednik utils.hash01 po stronie GPU.
   UWAGA: argumenty muszą być MAŁE. Przy liczbach rzędu dziesiątek
   tysięcy fract() zjada młodsze bity mantysy i sąsiednie pasy
   dostają skorelowane wartości — tarcza rwie się wtedy w regularny
   wzór zamiast losowo. Stąd indeks pasa osobno od zegara, a zegar
   zawijany przez mod(). */
float hash21(vec2 p) {
  vec3 p3 = fract(p.xyx * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

/* krycie tarczy w spróbkowanym miejscu (miękka krawędź na szerokość piksela) */
float disc(vec2 q, float aa) {
  return 1.0 - smoothstep(1.0 - aa, 1.0 + aa, length(q));
}

void main() {
  vec2 p = (vUv * 2.0 - 1.0) * uHalf;
  float aa = max(uPx.x, uPx.y) * 1.5;

  /* --- podział na pionowe pasy ---------------------------- */
  float n = max(4.0, uSlices);
  float bandW = 2.0 / n;
  float bi = clamp(floor((p.x + 1.0) / bandW), 0.0, n - 1.0);

  /* przetasowanie skokowe — obraz trzeszczy, zamiast falować */
  float stp = mod(floor(uTime * max(1.0, uSpeed)), 1024.0);
  float clk = stp + uSeed;                      /* zegar + ziarno glitcha */

  float h1 = hash21(vec2(bi,         clk));
  float h2 = hash21(vec2(bi + 311.0, clk));
  float h3 = hash21(vec2(bi + 719.0, clk));

  if (h2 < 0.09) discard;                       /* pas zgaszony */

  /* większość pasów tylko drga — kilka wyrywa się w pionie */
  float dy = (h1 * 2.0 - 1.0) * uStrength * (h2 < 0.42 ? 1.0 : 0.16);
  /* pojedyncze pasy rozciągają się poza sylwetę: sygnał "wycieka" */
  float hMul = h3 > 0.93 ? 1.0 + (h3 - 0.93) * 5.0 : 1.0;

  /* --- zaburzenie z poziomu piksela ------------------------ */
  /* drobny raster wzdłuż pasa: krawędź przestaje być linijką */
  float striate = hash21(vec2(floor(p.y * 190.0) + bi * 3.0, clk + 7.0)) - 0.5;
  float creep   = hash21(vec2(floor(p.y * 61.0) + bi * 5.0, clk + 23.0)) - 0.5;

  vec2 q = p;
  q.y -= dy;
  q.y += striate * uNoise * 0.07;
  q.y /= hMul;
  q.x += creep * uNoise * 0.02;

  float a = disc(q, aa);

  /* --- smuga: widmo pasa zostawione na dawnym miejscu ------- */
  float sm = uSmear * 0.42 * abs(dy);
  float ghost = disc(q + vec2(0.0, dy >= 0.0 ? sm : -sm), aa) * (1.0 - a);

  /* --- barwa ----------------------------------------------- */
  vec3 col = uOrange;
  if (h3 < uMono) col = h1 < 0.5 ? uBlack : uWhite;

  /* ziarno wygryzione w powierzchni pasa */
  float g = hash21(vec2(floor(p.x * 230.0) + bi, floor(p.y * 230.0) + clk));
  a *= 1.0 - uNoise * 0.5 * step(g, 0.3);

  /* smuga zawsze monochromatyczna — nigdy druga pomarańcz */
  if (ghost > 0.004) {
    col = h1 < 0.5 ? uBlack : uWhite;
    a = max(a, ghost * 0.5);
  }

  if (a <= 0.004) discard;
  gl_FragColor = vec4(col * a, a);              /* premultiplied alpha */
}
`;

/* barwa palety '#RRGGBB' → [0..1, 0..1, 0..1] */
const rgb = (hex) => [
  parseInt(hex.slice(1, 3), 16) / 255,
  parseInt(hex.slice(3, 5), 16) / 255,
  parseInt(hex.slice(5, 7), 16) / 255,
];

/* Rozmiar płótna zaokrąglany w górę do wielokrotności 64 — blisko
   potrzebnej rozdzielczości (potęgi dwójki potrafiły marnować pół
   płótna), a przy tym na tyle zgrubny, że przy jednej wielkości
   tarczy nie zmienia się z klatki na klatkę. */
const SIZE_GRID = 64;
const SIZE_MIN = 128;
const SIZE_MAX = 4096;   /* dodatkowo przycinane limitem sterownika */

class GlitchGL {

  constructor() {
    this.ok = false;
    this.canvas = null;
    this.gl = null;
    this._init();
  }

  _init() {
    if (!this.canvas) {
      /* płótno celowo NIE trafia do DOM — służy tylko jako źródło dla drawImage */
      this.canvas = document.createElement('canvas');
      this.canvas.width = this.canvas.height = SIZE_MIN;
      this._onLost = (e) => { e.preventDefault(); this.ok = false; };
      this._onRestored = () => { this.gl = null; this._init(); };
      this.canvas.addEventListener('webglcontextlost', this._onLost);
      this.canvas.addEventListener('webglcontextrestored', this._onRestored);
    }

    const opts = {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      /* czytamy bufor przez drawImage jeszcze w tym samym zadaniu —
         zachowanie bufora zdejmuje zależność od kolejności kompozycji */
      preserveDrawingBuffer: true,
      premultipliedAlpha: true,
    };
    const gl = this.canvas.getContext('webgl', opts)
      || this.canvas.getContext('experimental-webgl', opts);
    if (!gl) return;

    const prog = this._program(gl, VERT, FRAG);
    if (!prog) return;

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    gl.useProgram(prog);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.clearColor(0, 0, 0, 0);

    const u = {};
    for (const name of [
      'uHalf', 'uPx', 'uTime', 'uSeed', 'uSlices', 'uStrength',
      'uSpeed', 'uMono', 'uNoise', 'uSmear', 'uOrange', 'uBlack', 'uWhite',
    ]) u[name] = gl.getUniformLocation(prog, name);

    /* paleta jedzie raz — nie zmienia się w trakcie życia sceny */
    gl.uniform3fv(u.uOrange, rgb(PALETTE.orange));
    gl.uniform3fv(u.uBlack, rgb(PALETTE.black));
    gl.uniform3fv(u.uWhite, rgb(PALETTE.white));

    /* nie przekraczamy tego, co karta uniesie jako bufor rysowania */
    this.maxSize = Math.min(SIZE_MAX, gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || 2048);

    this.gl = gl;
    this.prog = prog;
    this.buf = buf;
    this.u = u;
    this.ok = true;
  }

  _program(gl, vsSrc, fsSrc) {
    const vs = this._shader(gl, gl.VERTEX_SHADER, vsSrc);
    const fs = this._shader(gl, gl.FRAGMENT_SHADER, fsSrc);
    if (!vs || !fs) return null;
    const p = gl.createProgram();
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      if (process.env.NODE_ENV === 'development') console.warn('[znaki] glitch: link', gl.getProgramInfoLog(p));
      return null;
    }
    return p;
  }

  _shader(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      if (process.env.NODE_ENV === 'development') console.warn('[znaki] glitch: shader', gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  /* Narysuj tarczę i oddaj płótno gotowe do drawImage().
     @param {Object} gl_ — pole spec.glitch (seed, slices…)
     @param {Object} G — PARAMS.glitch
     @param {Number} rCss — promień tarczy na ekranie [px CSS]
     @param {Number} dpr — gęstość pikseli płótna sceny
     @param {Number} t — czas sceny [s]
     @return {Object|null} { canvas, w, h } — rozmiar rysowania [px CSS] */
  render(gl_, G, rCss, dpr, t) {
    if (!this.ok || !this.gl) return null;
    const gl = this.gl;

    /* zasięg quada: w pionie musi zmieścić przesuw i rozciągnięcie pasa */
    const halfX = 1.06;
    const halfY = Math.min(2.6, 1.06 + Math.max(0, G.strength) * 1.36);

    const fit = (px) => Math.min(
      this.maxSize,
      Math.max(SIZE_MIN, Math.ceil(px / SIZE_GRID) * SIZE_GRID)
    );
    const cw = fit(2 * halfX * rCss * dpr);
    const ch = fit(2 * halfY * rCss * dpr);
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
    }
    gl.viewport(0, 0, cw, ch);

    const u = this.u;
    gl.uniform2f(u.uHalf, halfX, halfY);
    gl.uniform2f(u.uPx, (2 * halfX) / cw, (2 * halfY) / ch);
    gl.uniform1f(u.uTime, t);
    gl.uniform1f(u.uSeed, gl_.seed % 1024);   /* mały zakres — patrz uwaga przy hash21 */
    gl.uniform1f(u.uSlices, Math.max(4, gl_.slices));
    gl.uniform1f(u.uStrength, G.strength);
    gl.uniform1f(u.uSpeed, G.speed);
    gl.uniform1f(u.uMono, G.mono);
    gl.uniform1f(u.uNoise, G.noise);
    gl.uniform1f(u.uSmear, G.smear);

    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    return { canvas: this.canvas, w: 2 * halfX * rCss, h: 2 * halfY * rCss };
  }

  destroy() {
    this.ok = false;
    if (this.canvas) {
      this.canvas.removeEventListener('webglcontextlost', this._onLost);
      this.canvas.removeEventListener('webglcontextrestored', this._onRestored);
    }
    if (this.gl) {
      if (this.buf) this.gl.deleteBuffer(this.buf);
      if (this.prog) this.gl.deleteProgram(this.prog);
      const ext = this.gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    }
    this.gl = null;
    this.canvas = null;
  }
}

export default GlitchGL;
